import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Component } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
/**
 * Error Boundary component to catch and display React errors gracefully
 */
class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        Object.defineProperty(this, "handleReset", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.setState({
                    hasError: false,
                    error: null,
                    errorInfo: null
                });
            }
        });
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true };
    }
    componentDidCatch(error, errorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        this.setState({
            error,
            errorInfo
        });
        // Call optional error handler
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }
    render() {
        if (this.state.hasError) {
            // Use custom fallback if provided
            if (this.props.fallback) {
                return this.props.fallback;
            }
            // Default error UI
            return (_jsx("div", { className: "min-h-screen bg-background flex items-center justify-center p-4", children: _jsxs("div", { className: "max-w-md w-full bg-card rounded-lg shadow-lg p-6 border border-destructive/50", children: [_jsxs("div", { className: "flex items-center gap-3 mb-4", children: [_jsx(AlertTriangle, { className: "h-8 w-8 text-destructive" }), _jsx("h2", { className: "text-xl font-semibold", children: "Something went wrong" })] }), _jsx("p", { className: "text-muted-foreground mb-4", children: "An unexpected error occurred. Please try refreshing the page or contact support if the problem persists." }), this.state.error && (_jsxs("details", { className: "mb-4 p-3 bg-muted rounded border border-border", children: [_jsx("summary", { className: "cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground", children: "Error details" }), _jsxs("div", { className: "mt-2 space-y-2", children: [_jsxs("div", { children: [_jsx("p", { className: "text-xs font-semibold text-muted-foreground", children: "Error:" }), _jsx("p", { className: "text-xs text-destructive font-mono", children: this.state.error.toString() })] }), this.state.errorInfo && (_jsxs("div", { children: [_jsx("p", { className: "text-xs font-semibold text-muted-foreground", children: "Component stack:" }), _jsx("pre", { className: "text-xs text-muted-foreground overflow-auto max-h-32 font-mono", children: this.state.errorInfo.componentStack })] }))] })] })), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { onClick: this.handleReset, variant: "outline", className: "flex-1", children: "Try Again" }), _jsx(Button, { onClick: () => window.location.reload(), className: "flex-1", children: "Refresh Page" })] })] }) }));
        }
        return this.props.children;
    }
}
export default ErrorBoundary;
